import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatMoney, formatApiError, todayISO } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import {
  Phone, MessageSquare, CalendarClock, AlertTriangle, CheckCircle2,
  X, Save, Trash2, PlusCircle, ListFilter, History, Calendar as CalendarIcon,
  List as ListIcon, ChevronLeft, ChevronRight, ChevronDown, MapPin, Award, Mail,
} from "lucide-react";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths,
  format, isSameMonth, isSameDay, parseISO,
} from "date-fns";

const FILTERS = [
  { key: "due", label: "Due today / Overdue" },
  { key: "today", label: "Due today only" },
  { key: "overdue", label: "Overdue only" },
  { key: "upcoming", label: "Upcoming" },
  { key: "unscheduled", label: "No follow-up yet" },
  { key: "personal_visit", label: "Personal visit required" },
  { key: "all", label: "All pending" },
];

const AMOUNT_BANDS = [
  { key: "any",   label: "Any amount",       min: 0 },
  { key: "10k",   label: "Above ₹ 10,000",   min: 10000 },
  { key: "50k",   label: "Above ₹ 50,000",   min: 50000 },
  { key: "1L",    label: "Above ₹ 1 Lakh",   min: 100000 },
  { key: "1_5L",  label: "Above ₹ 1.5 Lakh", min: 150000 },
];

const rowEmails = (r) => {
  if (Array.isArray(r?.emails) && r.emails.length) return r.emails.filter(Boolean);
  if (r?.email) return [r.email];
  return [];
};

function EmailChips({ emails, testId }) {
  if (!emails || emails.length === 0) return null;
  const subject = "Payment reminder";
  return (
    <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-gray-500 mt-0.5" data-testid={testId}>
      <Mail size={11} className="text-gray-400 shrink-0" />
      {emails.map((e, i) => (
        <a key={i} href={`mailto:${e}?subject=${encodeURIComponent(subject)}`}
          className="hover:text-black hover:underline break-all"
          title={`Send email to ${e}`}>{e}</a>
      ))}
    </div>
  );
}

function LogModal({ row, onClose, onSaved }) {
  const [comment, setComment] = useState("");
  const [mode, setMode] = useState("call");
  const [nextDate, setNextDate] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() + 3);
    return d.toISOString().slice(0, 10);
  });
  const [status, setStatus] = useState(row.status || "open");
  const [busy, setBusy] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/followups/${row.party_id}/log`, {
        comment, mode, next_followup_date: nextDate || null, status,
      });
      toast.success("Follow-up logged");
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-sm w-full max-w-lg max-h-[92vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="followup-modal">
        <div className="p-5 border-b border-gray-200 flex items-start justify-between sticky top-0 bg-white z-10">
          <div className="min-w-0">
            <div className="small-label text-gray-500">Log follow-up · #{(row.followup_count || 0) + 1}</div>
            <div className="font-heading font-black text-xl truncate">{row.party_name}</div>
            <div className="text-xs text-gray-500 mt-1">
              Pending <span className="data-num font-bold text-[#B45309]">₹ {formatMoney(row.balance)}</span>
              {row.phone ? <> · {row.phone}</> : null}
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-sm"><X size={20} /></button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="small-label block mb-1.5">How did you contact them?</label>
            <div className="flex items-center gap-2 flex-wrap">
              {[
                { key: "call", label: "Call", icon: Phone },
                { key: "whatsapp", label: "WhatsApp", icon: MessageSquare },
                { key: "personal_visit", label: "Personal visit", icon: MapPin },
                { key: "other", label: "Other", icon: ChevronDown },
              ].map((m) => (
                <button key={m.key} type="button"
                  onClick={() => setMode(m.key)}
                  className={`h-10 px-4 text-xs font-semibold rounded-sm border flex items-center gap-1.5 ${mode === m.key ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
                  data-testid={`followup-mode-${m.key}`}>
                  <m.icon size={13} /> {m.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="small-label block mb-1.5">What was the outcome / next step?</label>
            <textarea
              className="input-lg min-h-[100px] py-2"
              placeholder="e.g. spoke with Rohit, promised payment by Friday"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              data-testid="followup-comment"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="small-label block mb-1.5">Next follow-up date</label>
              <input type="date" className="input-lg" value={nextDate}
                onChange={(e) => setNextDate(e.target.value)}
                data-testid="followup-next-date" />
            </div>
            <div>
              <label className="small-label block mb-1.5">Status</label>
              <select className="input-lg" value={status}
                onChange={(e) => setStatus(e.target.value)}
                data-testid="followup-status">
                <option value="open">Open · still chasing</option>
                <option value="closed">Closed · settled / on hold</option>
              </select>
            </div>
          </div>

          {row.history?.length > 0 && (
            <div className="card-flat p-3">
              <button onClick={() => setShowHistory((s) => !s)} className="text-xs text-gray-500 hover:text-black flex items-center gap-1.5 mb-2">
                <History size={13} /> {showHistory ? "Hide" : "Show"} previous {row.history.length} follow-up{row.history.length > 1 ? "s" : ""}
              </button>
              {showHistory && (
                <div className="space-y-2 max-h-48 overflow-auto">
                  {[...row.history].reverse().map((h, i) => (
                    <div key={i} className="text-xs border-l-2 border-gray-200 pl-3 py-1">
                      <div className="flex items-center justify-between">
                        <span className="font-semibold flex items-center gap-1.5">
                          {h.date}
                          {h.mode && (
                            <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${h.mode === "personal_visit" ? "bg-[#DBEAFE] text-[#1D4ED8]" : "bg-gray-100 text-gray-600"}`}>
                              {h.mode === "call" && <Phone size={9} />}
                              {h.mode === "whatsapp" && <MessageSquare size={9} />}
                              {h.mode === "personal_visit" && <MapPin size={9} />}
                              {h.mode.replace("_", " ")}
                            </span>
                          )}
                        </span>
                        <span className="text-gray-500">{h.by_email}</span>
                      </div>
                      <div className="text-gray-700 break-words">{h.comment || <em className="text-gray-400">no comment</em>}</div>
                      {h.next_followup_date && <div className="text-gray-500">next: {h.next_followup_date}</div>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-5 border-t border-gray-200 sticky bottom-0 bg-white flex justify-end gap-2">
          <button onClick={onClose} className="h-11 px-5 border border-gray-300 rounded-sm font-semibold text-sm">Cancel</button>
          <button onClick={save} disabled={busy} className="btn-primary h-11 px-6 flex items-center gap-2" data-testid="followup-save">
            <Save size={16} /> {busy ? "Saving…" : "Log Follow-up"}
          </button>
        </div>
      </div>
    </div>
  );
}

const RATING_STYLE = {
  A: { cls: "bg-[#DCFCE7] text-[#15803D]", tip: "Pays on time — no follow-up needed" },
  B: { cls: "bg-[#DBEAFE] text-[#1D4ED8]", tip: "2-3 follow-ups needed" },
  C: { cls: "bg-[#FEF3C7] text-[#B45309]", tip: "Multiple follow-ups needed" },
  D: { cls: "bg-[#FEE2E2] text-[#B91C1C]", tip: "No response — personal visit required" },
};

function CreditRatingBadge({ value, partyId, onChanged }) {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const set = async (val) => {
    setSaving(true);
    try {
      await api.patch(`/parties/${partyId}/credit-rating`, { credit_rating: val });
      toast.success(val ? `Credit rating set to ${val}` : "Rating cleared");
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); setEditing(false); }
  };
  if (editing) {
    return (
      <select
        autoFocus
        className="border border-gray-300 rounded-sm text-[10px] px-1 py-0.5"
        value={value || ""}
        onBlur={() => setEditing(false)}
        onChange={(e) => set(e.target.value)}
        disabled={saving}
        data-testid={`rating-edit-${partyId}`}
      >
        <option value="">— rate —</option>
        <option value="A">A · on time</option>
        <option value="B">B · 2-3</option>
        <option value="C">C · many</option>
        <option value="D">D · visit</option>
      </select>
    );
  }
  const style = value ? RATING_STYLE[value] : null;
  return (
    <button
      onClick={(e) => { e.preventDefault(); setEditing(true); }}
      className={`inline-flex items-center gap-1 text-[10px] font-bold rounded-sm px-1.5 py-0.5 ${style ? style.cls : "border border-dashed border-gray-300 text-gray-400 hover:text-black"}`}
      title={style ? style.tip : "Click to rate customer credit"}
      data-testid={`rating-badge-${partyId}`}
    >
      <Award size={9} /> {value || "Rate"}
    </button>
  );
}

function CalendarView({ rows, cursorMonth, setCursorMonth, onDayClick, today }) {
  // Group rows by due_date
  const byDate = useMemo(() => {
    const m = {};
    for (const r of rows) {
      const d = r.due_date;
      if (!d) continue;
      (m[d] ||= []).push(r);
    }
    return m;
  }, [rows]);

  const monthStart = startOfMonth(cursorMonth);
  const monthEnd = endOfMonth(cursorMonth);
  const gridStart = startOfWeek(monthStart, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(monthEnd, { weekStartsOn: 1 });
  const days = [];
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) days.push(d);

  const monthTotal = useMemo(() => {
    let total = 0, count = 0;
    for (const d of days) {
      if (!isSameMonth(d, cursorMonth)) continue;
      const list = byDate[format(d, "yyyy-MM-dd")] || [];
      count += list.length;
      total += list.reduce((s, r) => s + (r.balance || 0), 0);
    }
    return { total, count };
  }, [days, byDate, cursorMonth]);

  return (
    <div className="card-flat" data-testid="followup-calendar">
      <div className="p-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setCursorMonth(addMonths(cursorMonth, -1))}
            className="h-9 w-9 flex items-center justify-center border border-gray-300 rounded-sm hover:bg-gray-50"
            data-testid="calendar-prev-month"
          ><ChevronLeft size={16} /></button>
          <div className="font-heading font-black text-xl min-w-[180px] text-center" data-testid="calendar-month-label">
            {format(cursorMonth, "MMMM yyyy")}
          </div>
          <button
            onClick={() => setCursorMonth(addMonths(cursorMonth, 1))}
            className="h-9 w-9 flex items-center justify-center border border-gray-300 rounded-sm hover:bg-gray-50"
            data-testid="calendar-next-month"
          ><ChevronRight size={16} /></button>
          <button
            onClick={() => setCursorMonth(startOfMonth(new Date()))}
            className="h-9 px-3 text-xs font-semibold border border-gray-300 rounded-sm hover:bg-gray-50 ml-1"
            data-testid="calendar-today-btn"
          >Today</button>
        </div>
        <div className="text-xs text-gray-600">
          <span className="font-semibold">{monthTotal.count}</span> follow-ups scheduled in month
          {" · "}Total ₹ <span className="data-num font-bold text-[#B45309]">{formatMoney(monthTotal.total)}</span>
        </div>
      </div>

      <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50">
        {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => (
          <div key={d} className="small-label text-gray-500 py-2 text-center border-r border-gray-200 last:border-r-0">{d}</div>
        ))}
      </div>

      <div className="grid grid-cols-7">
        {days.map((d) => {
          const key = format(d, "yyyy-MM-dd");
          const list = byDate[key] || [];
          const inMonth = isSameMonth(d, cursorMonth);
          const isToday = key === today;
          const isPast = key < today;
          const hasDues = list.length > 0;
          const dayTotal = list.reduce((s, r) => s + (r.balance || 0), 0);
          const cellBg = !inMonth
            ? "bg-gray-50/50 text-gray-300"
            : isToday
              ? "bg-[#FEF3C7]"
              : (hasDues && isPast)
                ? "bg-[#FEE2E2]/50"
                : "bg-white";
          return (
            <button
              key={key}
              onClick={() => hasDues && onDayClick(key, list)}
              disabled={!hasDues}
              className={`min-h-[110px] border-r border-b border-gray-200 last:border-r-0 p-2 text-left align-top ${cellBg} ${hasDues ? "hover:bg-gray-50 cursor-pointer" : "cursor-default"}`}
              data-testid={`calendar-day-${key}`}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={`text-xs font-bold ${isToday ? "text-[#B45309]" : inMonth ? "text-gray-800" : "text-gray-300"}`}>
                  {format(d, "d")}
                </span>
                {hasDues && (
                  <span className={`text-[10px] px-1.5 py-0.5 rounded-sm font-bold ${isPast ? "bg-[#B91C1C] text-white" : isToday ? "bg-[#B45309] text-white" : "bg-[#15803D] text-white"}`}>
                    {list.length}
                  </span>
                )}
              </div>
              <div className="space-y-0.5 overflow-hidden">
                {list.slice(0, 3).map((r) => (
                  <div key={r.party_id} className="text-[11px] truncate leading-tight text-gray-700" title={`${r.party_name} · ₹${formatMoney(r.balance)}`}>
                    <span className="font-medium">{r.party_name}</span>
                    <span className="text-gray-400"> · ₹{formatMoney(r.balance)}</span>
                  </div>
                ))}
                {list.length > 3 && (
                  <div className="text-[10px] text-gray-500 font-semibold">+{list.length - 3} more</div>
                )}
              </div>
              {hasDues && (
                <div className="text-[10px] text-[#B45309] font-bold mt-1 data-num">₹ {formatMoney(dayTotal)}</div>
              )}
            </button>
          );
        })}
      </div>

      <div className="p-3 border-t border-gray-200 flex items-center gap-4 flex-wrap text-xs text-gray-500">
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-[#FEE2E2]/70 border border-[#FECACA]" /> Overdue day</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-[#FEF3C7] border border-[#FDE68A]" /> Today</span>
        <span className="flex items-center gap-1.5"><span className="inline-block w-3 h-3 bg-white border border-gray-300" /> Upcoming</span>
        <span className="ml-auto">Click a day to see all parties due · Auto-computed from oldest unpaid challan + credit period</span>
      </div>
    </div>
  );
}

function DayDrawer({ date, rows, onClose, onLog }) {
  const total = rows.reduce((s, r) => s + (r.balance || 0), 0);
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-sm sm:rounded-sm w-full sm:max-w-2xl max-h-[85vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="day-drawer">
        <div className="p-5 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div>
            <div className="small-label text-gray-500">Parties due on</div>
            <div className="font-heading font-black text-xl">{format(parseISO(date), "EEE, dd MMM yyyy")}</div>
            <div className="text-xs text-gray-500 mt-1">
              {rows.length} part{rows.length === 1 ? "y" : "ies"} · Total ₹ <span className="data-num font-bold text-[#B45309]">{formatMoney(total)}</span>
            </div>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded-sm"><X size={20} /></button>
        </div>
        <div className="divide-y divide-gray-100">
          {rows.map((r) => (
            <div key={r.party_id} className="p-4 flex items-center gap-3 hover:bg-gray-50" data-testid={`day-drawer-row-${r.party_id}`}>
              <div className="flex-1 min-w-0">
                <Link to={`/parties/${r.party_id}`} className="font-semibold hover:underline block truncate">{r.party_name}</Link>
                <div className="text-xs text-gray-500">
                  Pending <span className="data-num font-bold text-[#B45309]">₹ {formatMoney(r.balance)}</span>
                  {r.oldest_unpaid_date && <> · oldest bill {r.oldest_unpaid_date}</>}
                  {r.days_overdue > 0 && <> · <span className="text-[#B91C1C] font-bold">{r.days_overdue}d overdue</span></>}
                </div>
                <EmailChips emails={rowEmails(r)} testId={`day-drawer-emails-${r.party_id}`} />
              </div>
              <div className="flex items-center gap-2">
                {r.phone && (
                  <>
                    <a href={`tel:${r.phone}`} className="text-gray-500 hover:text-black p-1.5" title="Call"><Phone size={14} /></a>
                    <a href={`https://wa.me/${r.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Hello ${r.party_name}, gentle reminder regarding pending payment of ₹ ${formatMoney(r.balance)}.`)}`}
                       target="_blank" rel="noreferrer" className="text-gray-500 hover:text-[#15803D] p-1.5" title="WhatsApp"><MessageSquare size={14} /></a>
                  </>
                )}
                <button onClick={() => onLog(r)} className="btn-primary h-8 px-3 text-xs flex items-center gap-1.5" data-testid={`day-drawer-log-${r.party_id}`}>
                  <PlusCircle size={13} /> Log
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function PaymentFollowups() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("due");
  const [amountBand, setAmountBand] = useState("any");
  const [onDate, setOnDate] = useState(todayISO());
  const [loading, setLoading] = useState(true);
  const [modalRow, setModalRow] = useState(null);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("list"); // "list" | "calendar"
  const [cursorMonth, setCursorMonth] = useState(() => startOfMonth(new Date()));
  const [drawerDate, setDrawerDate] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/followups", { params: { status: "open" } });
      setData(data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const today = todayISO();

  const rows = useMemo(() => {
    let r = data?.rows || [];
    const q = search.trim().toLowerCase();
    if (q) r = r.filter((x) => x.party_name.toLowerCase().includes(q));
    switch (filter) {
      case "today":
        r = r.filter((x) => x.due_date === onDate); break;
      case "overdue":
        r = r.filter((x) => x.due_date && x.due_date < onDate); break;
      case "due":
        r = r.filter((x) => x.due_date && x.due_date <= onDate); break;
      case "upcoming":
        r = r.filter((x) => x.due_date && x.due_date > onDate); break;
      case "unscheduled":
        r = r.filter((x) => !x.due_date); break;
      case "personal_visit":
        r = r.filter((x) => x.credit_rating === "D"); break;
      default: /* "all" */ break;
    }
    const band = AMOUNT_BANDS.find((b) => b.key === amountBand);
    if (band && band.min > 0) {
      r = r.filter((x) => (x.balance || 0) >= band.min);
    }
    return r;
  }, [data, filter, amountBand, onDate, search]);

  const totals = useMemo(() => ({
    pending: rows.reduce((s, r) => s + (r.balance || 0), 0),
    count: rows.length,
  }), [rows]);

  const tagFor = (r) => {
    if (!r.due_date) return { cls: "bg-gray-100 text-gray-600", txt: "No due date" };
    if (r.due_date < today) return { cls: "bg-[#FEE2E2] text-[#B91C1C]", txt: `Overdue · ${r.days_overdue || 0}d` };
    if (r.due_date === today) return { cls: "bg-[#FEF3C7] text-[#B45309]", txt: "Due today" };
    return { cls: "bg-[#DCFCE7] text-[#15803D]", txt: "Upcoming" };
  };

  const removeFollowup = async (id) => {
    if (!window.confirm("Remove follow-up tracker for this party? History will be lost.")) return;
    try {
      await api.delete(`/followups/${id}`);
      toast.success("Follow-up cleared");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="space-y-6" data-testid="payment-followups-page">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="card-flat p-5">
          <div className="small-label text-gray-500">Tracked pending</div>
          <div className="data-num text-3xl mt-1" data-testid="followup-total-pending">
            ₹ {formatMoney(totals.pending)}
          </div>
          <div className="text-xs text-gray-500 mt-1">{totals.count} part{totals.count === 1 ? "y" : "ies"}</div>
        </div>
        <div className="card-flat p-5 bg-[#FEE2E2] border-[#FECACA]">
          <div className="small-label text-[#B91C1C] flex items-center gap-1.5"><AlertTriangle size={12} /> Overdue</div>
          <div className="data-num text-3xl mt-1 text-[#B91C1C]" data-testid="followup-overdue-count">
            {data?.summary?.overdue_count || 0}
          </div>
          <div className="text-xs text-[#B91C1C]/80 mt-1">Past due date</div>
        </div>
        <div className="card-flat p-5 bg-[#FEF3C7] border-[#FDE68A]">
          <div className="small-label text-[#B45309] flex items-center gap-1.5"><CalendarClock size={12} /> Due today</div>
          <div className="data-num text-3xl mt-1 text-[#B45309]" data-testid="followup-due-today-count">
            {data?.summary?.due_today_count || 0}
          </div>
          <div className="text-xs text-[#B45309]/80 mt-1">Scheduled for {today}</div>
        </div>
        <div className="card-flat p-5">
          <div className="small-label text-gray-500 flex items-center gap-1.5"><PlusCircle size={12} /> Unscheduled</div>
          <div className="data-num text-3xl mt-1" data-testid="followup-unscheduled-count">
            {data?.summary?.unscheduled_count || 0}
          </div>
          <div className="text-xs text-gray-500 mt-1">Pending but no follow-up yet</div>
        </div>
      </div>

      {/* View toggle */}
      <div className="flex items-center gap-2">
        <div className="inline-flex bg-white border border-gray-300 rounded-sm p-0.5">
          <button
            onClick={() => setView("list")}
            className={`h-9 px-4 text-xs font-semibold flex items-center gap-1.5 rounded-sm ${view === "list" ? "bg-black text-white" : "text-gray-600 hover:text-black"}`}
            data-testid="view-toggle-list"
          ><ListIcon size={13} /> List</button>
          <button
            onClick={() => setView("calendar")}
            className={`h-9 px-4 text-xs font-semibold flex items-center gap-1.5 rounded-sm ${view === "calendar" ? "bg-black text-white" : "text-gray-600 hover:text-black"}`}
            data-testid="view-toggle-calendar"
          ><CalendarIcon size={13} /> Calendar</button>
        </div>
      </div>

      {view === "calendar" ? (
        <CalendarView
          rows={data?.rows || []}
          cursorMonth={cursorMonth}
          setCursorMonth={setCursorMonth}
          onDayClick={(d) => setDrawerDate(d)}
          today={today}
        />
      ) : (
        <>
      {/* Controls */}
      <div className="card-flat p-4 space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap">
            <ListFilter size={14} className="text-gray-500" />
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`px-3 h-9 rounded-sm text-xs font-semibold border ${filter === f.key ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
                data-testid={`filter-${f.key}`}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="ml-auto flex items-center gap-3 flex-wrap">
            <label className="small-label text-gray-500">View follow-ups for</label>
            <input type="date" className="input-lg w-auto h-9" value={onDate}
              onChange={(e) => setOnDate(e.target.value)}
              data-testid="followup-on-date" />
            <input
              className="input-lg h-9 w-44"
              placeholder="Search party…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="followup-search"
            />
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap pt-2 border-t border-gray-100">
          <span className="small-label text-gray-500 mr-1">Amount:</span>
          {AMOUNT_BANDS.map((b) => (
            <button
              key={b.key}
              onClick={() => setAmountBand(b.key)}
              className={`px-3 h-8 rounded-sm text-xs font-semibold border ${amountBand === b.key ? "bg-black text-white border-black" : "bg-white text-gray-600 border-gray-300 hover:border-black"}`}
              data-testid={`amount-${b.key}`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      <div className="card-flat overflow-auto">
        <table className="table-dense w-full">
          <thead>
            <tr>
              <th className="text-left">Status</th>
              <th className="text-left">Party</th>
              <th className="num">Pending ₹</th>
              <th className="text-left">Last</th>
              <th className="text-left">Due</th>
              <th className="num">Count</th>
              <th className="text-left">Last comment</th>
              <th className="text-right"></th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="8" className="py-8 text-center text-sm text-gray-400">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="8" className="py-8 text-center text-sm text-gray-400">
                <CheckCircle2 size={28} className="inline-block text-[#15803D] mb-1.5" />
                <div>No follow-ups match this filter.</div>
              </td></tr>
            )}
            {!loading && rows.map((r) => {
              const tag = tagFor(r);
              return (
                <tr key={r.party_id} data-testid={`followup-row-${r.party_id}`}>
                  <td>
                    <span className={`px-2 py-0.5 text-xs rounded-sm font-semibold ${tag.cls}`}>{tag.txt}</span>
                  </td>
                  <td>
                    <Link to={`/parties/${r.party_id}`} className="font-semibold hover:underline">{r.party_name}</Link>
                    <div className="text-xs text-gray-500 capitalize flex items-center gap-1.5">
                      <span>{r.party_type}{r.phone ? ` · ${r.phone}` : ""}</span>
                      <CreditRatingBadge value={r.credit_rating} partyId={r.party_id} onChanged={load} />
                    </div>
                    <EmailChips emails={rowEmails(r)} testId={`followup-emails-${r.party_id}`} />
                  </td>
                  <td className="num font-bold text-[#B45309]">{formatMoney(r.balance)}</td>
                  <td className="data-num text-xs">{r.last_followup_date || "—"}</td>
                  <td className="data-num text-xs">
                    {r.due_date ? (
                      <span className="inline-flex flex-col leading-tight">
                        <span>{r.due_date}</span>
                        {!r.next_followup_date && r.auto_due_date && (
                          <span className="text-[10px] text-gray-400">auto · credit +{r.credit_period_days}d</span>
                        )}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="num">{r.followup_count}</td>
                  <td className="text-xs text-gray-700 max-w-[260px] truncate" title={r.last_comment}>
                    {r.last_comment || <span className="text-gray-400">—</span>}
                  </td>
                  <td className="text-right">
                    <div className="inline-flex items-center gap-2">
                      {r.phone && (
                        <>
                          <a href={`tel:${r.phone}`} className="text-gray-500 hover:text-black" title="Call" data-testid={`followup-call-${r.party_id}`}>
                            <Phone size={14} />
                          </a>
                          <a href={`https://wa.me/${r.phone.replace(/\D/g, "")}?text=${encodeURIComponent(`Hello ${r.party_name}, gentle reminder regarding pending payment of ₹ ${formatMoney(r.balance)}.`)}`}
                             target="_blank" rel="noreferrer"
                             className="text-gray-500 hover:text-[#15803D]" title="WhatsApp">
                            <MessageSquare size={14} />
                          </a>
                        </>
                      )}
                      <button onClick={() => setModalRow(r)} className="btn-primary h-8 px-3 text-xs flex items-center gap-1.5" data-testid={`followup-log-${r.party_id}`}>
                        <PlusCircle size={13} /> Log
                      </button>
                      {isAdmin && r.id && (
                        <button onClick={() => removeFollowup(r.id)} className="text-gray-400 hover:text-[#B91C1C]" title="Clear follow-up" data-testid={`followup-del-${r.party_id}`}>
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
        </>
      )}

      {drawerDate && (
        <DayDrawer
          date={drawerDate}
          rows={(data?.rows || []).filter((r) => r.due_date === drawerDate)}
          onClose={() => setDrawerDate(null)}
          onLog={(r) => { setDrawerDate(null); setModalRow(r); }}
        />
      )}

      {modalRow && (
        <LogModal
          row={modalRow}
          onClose={() => setModalRow(null)}
          onSaved={() => { setModalRow(null); load(); }}
        />
      )}
    </div>
  );
}
